import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSubscription } from "../../context/SubscriptionContext";
import { cn } from "../../lib/utils";
import {
  LayoutDashboard,
  Users,
  User,
  FileText,
  Calendar,
  LogOut,
  Briefcase,
  ClipboardList,
  HeartPulse,
  BarChart3,
  Car,
  Package,
  Receipt,
  DollarSign,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Camera,
  Settings2,
  Landmark,
  FolderOpen,
  CreditCard,
  Wallet,
  Bell,
  BookOpen,
  UsersRound,
  Eye,
  X,
  Tag,
  Award,
  GraduationCap,
  Shield,
  Crown,
  Lock,
  Zap,
  Rocket,
  Mail,
  Truck,
  Calculator,
  Sparkles,
  Wrench,
  Fuel,
  ShieldCheck,
  TrendingUp,
  Building2,
  HandCoins,
  UserCog,
  UserPlus,
  Target,
  Heart,
  PiggyBank,
  Banknote,
  FileSpreadsheet,
  FilePieChart,
  Construction,
  Monitor,
  FileBadge,
  Lightbulb,
  StickyNote,
  KeyRound,
  TestTube2,
  ScrollText,
  Megaphone,
  Database,
  MessageSquare,
  FileStack,
  Upload,
  ClipboardCheck,
  Clock,
  Repeat,
  Image,
  Globe,
  BellRing,
  LineChart,
  RefreshCcw,
} from "lucide-react";
import { UnderConstructionDialog } from "../UnderConstructionDialog";
import { AvatarUploadDialog } from "../AvatarUploadDialog";
import { toast } from "sonner";

/*
  MODULE_HIERARCHY defines the entire sidebar structure.
  - Each top-level key is a module group (Operations, Employees, etc.)
  - `items` are direct nav links
  - `children` on an item create a nested sub-section
  - `underConstruction: true` triggers the UC popup instead of navigation
*/
const MODULE_HIERARCHY = [
  {
    key: "operations",
    name: "Operations",
    icon: Briefcase,
    color: "text-emerald-600",
    bgColor: "bg-emerald-100",
    defaultExpanded: true,
    items: [
      { label: "Customize Business", icon: Settings2, href: "/customization", feature: null, permission: "can_manage_employees" },
      { label: "Email", icon: Mail, href: "/email-templates", feature: null, permission: "can_message" },
      { label: "Calendar", icon: Calendar, href: "/calendar", feature: null, permission: "can_view_schedule" },
      {
        label: "Clients",
        icon: User,
        href: "/clients",
        feature: "clients",
        permission: "can_view_clients",
        children: [
          { label: "Client Profiles", icon: User, href: "/clients", feature: "clients", permission: "can_view_clients" },
        ],
      },
      { label: "Services & Goods", icon: Briefcase, href: "/services", feature: "services_goods" },
      { label: "Price Lists", icon: Tag, href: "/price-lists", feature: "services_goods", permission: "can_manage_services" },
      { label: "Memberships", icon: Repeat, href: "/memberships", feature: "services_goods", permission: "can_manage_services" },
      { label: "Inventory", icon: Package, href: "/inventory", feature: "services_goods" },
      { label: "Parts & Procurement", icon: Wrench, href: "/parts-procurement", feature: null, badge: "UC" },
      { label: "Quotes", icon: FileText, href: "/quotes", feature: null, permission: "can_view_quotes" },
      { label: "Invoices", icon: Receipt, href: "/invoices", feature: "invoicing", permission: "can_view_invoices" },
    ],
  },
  {
    key: "accounting_pro",
    name: "Accounting Pro (CA)",
    icon: Landmark,
    color: "text-emerald-700",
    bgColor: "bg-emerald-100",
    defaultExpanded: false,
    // iter150 — Full accounting module. Whole group is a paid add-on
    // (accounting_pro). Sidebar shows the group regardless so users can
    // browse the scope even before subscribing; the top of the module
    // shows an Upgrade card when they don't have the add-on.
    items: [
      { label: "Overview", icon: Sparkles, href: "/accounting", feature: null, badge: "UC" },
      { label: "Chart of Accounts", icon: BookOpen, href: "/accounting/chart-of-accounts", feature: null, badge: "UC" },
      { label: "Journal Entries", icon: FileText, href: "/accounting/journal-entries", feature: null, badge: "UC" },
      { label: "General Ledger", icon: ScrollText, href: "/accounting/general-ledger", feature: null, badge: "UC" },
      { label: "Financial Reports", icon: LineChart, href: "/accounting/reports", feature: null, badge: "UC" },
      { label: "Vendors & Suppliers", icon: Users, href: "/accounting/vendors", feature: null, badge: "UC" },
      { label: "Vendor Bills (AP)", icon: Receipt, href: "/accounting/vendor-bills", feature: null, badge: "UC" },
      { label: "Cheques", icon: FileText, href: "/accounting/cheques", feature: null, badge: "UC" },
      { label: "Bank Accounts", icon: Landmark, href: "/accounting/bank-accounts", feature: null, badge: "UC" },
      { label: "Bank Reconciliation", icon: RefreshCcw, href: "/accounting/reconciliation", feature: null, badge: "UC" },
      { label: "Franchise & Branches", icon: Building2, href: "/accounting/branches", feature: null, badge: "UC" },
      { label: "Health Score", icon: HeartPulse, href: "/accounting/health-score", feature: null, badge: "UC" },
      { label: "Bulk Upload / Migration", icon: Upload, href: "/accounting/bulk-upload", feature: null, badge: "UC" },
    ],
  },
  {
    key: "employees",
    name: "Employees",
    icon: Users,
    color: "text-blue-600",
    bgColor: "bg-blue-100",
    defaultExpanded: false,
    items: [
      { label: "Employee Profiles", icon: Users, href: "/employees", feature: "employees", permission: "can_manage_employees" },
      { label: "My Profile", icon: UserCog, href: "/my-profile", feature: null, permission: "can_view_own_profile" },
      { label: "Onboarding", icon: UserPlus, href: "/onboarding", feature: "employees", permission: "can_view_onboarding" },
      { label: "Training", icon: GraduationCap, href: "/training", feature: "training", permission: "can_view_training" },
      { label: "Performance & Development", icon: Target, href: "/performance", feature: "employees", permission: "can_manage_employees" },
      { label: "Time Clock", icon: Clock, href: "/time-clock", feature: null, permission: "can_view_own_profile" },
      {
        label: "Payroll & Compensation",
        icon: DollarSign,
        href: "/payroll",
        feature: "payroll",
        permission: "can_view_pay_summary",
        children: [
          { label: "Pay Stubs", icon: Receipt, href: "/pay-stubs", feature: "payroll", permission: "can_view_pay_stubs" },
          { label: "Pay Engine", icon: Calculator, href: "/pay-engine", feature: "payroll", permission: "can_view_pay_summary" },
          { label: "Tax Documents", icon: FileText, href: "/tax-documents", feature: "tax_settings" },
          { label: "ROEs", icon: FileSpreadsheet, href: "/roes", feature: "payroll" },
          { label: "Summaries", icon: FilePieChart, href: "/payroll-summaries", feature: "payroll", permission: "can_view_pay_summary" },
        ],
      },
      { label: "Benefits", icon: Heart, href: "/benefits", feature: "employees" },
      { label: "Pensions", icon: PiggyBank, href: "/pensions", feature: "employees" },
    ],
  },
  {
    key: "client_portal_mgmt",
    name: "Client Portals",
    icon: Monitor,
    color: "text-pink-600",
    bgColor: "bg-pink-100",
    defaultExpanded: false,
    items: [
      { label: "Manage Client Portals", icon: Monitor, href: "/client-portal-management", feature: "clients", permission: "can_manage_employees" },
    ],
  },
  {
    key: "employee_portal_mgmt",
    name: "Employee Portals",
    icon: UserCog,
    color: "text-indigo-600",
    bgColor: "bg-indigo-100",
    defaultExpanded: false,
    items: [
      { label: "Manage Employee Portals", icon: UserCog, href: "/portal-settings", feature: "portal_management", permission: "can_manage_employees" },
      { label: "Customization Center", icon: Settings2, href: "/customization", feature: null, permission: "can_manage_employees" },
    ],
  },
  {
    key: "financials",
    name: "Financials",
    icon: Banknote,
    color: "text-amber-600",
    bgColor: "bg-amber-100",
    defaultExpanded: false,
    items: [
      { label: "Accounts Receivable", icon: Wallet, href: "/accounts-receivable", feature: "invoicing", permission: "can_view_invoices" },
      { label: "Accounts Payable", icon: CreditCard, href: "/accounts-payable", feature: "invoicing", permission: "can_view_invoices" },
      { label: "Business Expenses", icon: Receipt, href: "/expenses", feature: null, permission: "can_view_invoices" },
      { label: "Job Cost vs Budget", icon: Banknote, href: "/job-budgets", feature: null, permission: "can_view_invoices" },
      { label: "Banking & Credit", icon: Building2, href: "/banking-credit", feature: "invoicing", permission: "can_view_reports" },
      { label: "Tax & Compliance", icon: ShieldCheck, href: "/tax-compliance", feature: "tax_settings", permission: "can_view_reports" },
      { label: "Reporting", icon: BarChart3, href: "/analytics", feature: "basic_reports", permission: "can_view_reports" },
    ],
  },
  {
    key: "administration",
    name: "Administration",
    icon: Settings2,
    color: "text-purple-600",
    bgColor: "bg-purple-100",
    defaultExpanded: false,
    items: [
      { label: "Business Profile", icon: Landmark, href: "/business-profile", feature: null },
      { label: "Document Hub", icon: FolderOpen, href: "/documents", feature: "documents", permission: "can_view_documents" },
      { label: "Operational Docs", icon: ClipboardCheck, href: "/operational-docs", feature: null, permission: "can_manage_employees" },
      { label: "Job Photos & Annotation", icon: Image, href: "/job-photos", feature: null, permission: "can_view_jobs" },
      { label: "Notification Settings", icon: Bell, href: "/notification-settings", feature: null, permission: "can_manage_employees" },
      { label: "Recruitment", icon: Briefcase, href: "/recruitment", feature: null, permission: "can_manage_employees" },
      { label: "Access Requests", icon: Lock, href: "/access-requests", feature: null },
      { label: "Year-over-Year Audit", icon: TrendingUp, href: "/audit-yoy", feature: null, permission: "can_manage_employees" },
      { label: "Public Catalog", icon: Tag, href: "/public-catalog", feature: null, permission: "can_manage_employees" },
      { label: "Team Chat", icon: Mail, href: "/team-chat", feature: null },
      { label: "PDF Templates", icon: FileText, href: "/pdf-templates", feature: null, permission: "can_manage_employees" },
      { label: "Coupons & Promos", icon: Tag, href: "/coupons", feature: null, permission: "can_manage_employees" },
      { label: "Marketing", icon: Mail, href: "/marketing", feature: null, permission: "can_manage_employees" },
      { label: "Localization", icon: Globe, href: "/localization", feature: null, permission: "can_manage_employees" },
      { label: "Pending Sign-offs", icon: BellRing, href: "/admin/pending-acknowledgments", feature: null, permission: "can_manage_employees" },
      {
        label: "Compliance",
        icon: Shield,
        href: "/safety-documents",
        feature: "safety",
        permission: "can_view_safety_docs",
        children: [
          { label: "Health & Safety", icon: HeartPulse, href: "/safety-documents", feature: "safety", permission: "can_view_safety_docs" },
          { label: "Policies & Procedures", icon: BookOpen, href: "/policies", feature: "policies", permission: "can_view_policies" },
          { label: "Terms & Conditions", icon: Shield, href: "/terms-conditions", feature: null },
          { label: "Certifications", icon: Award, href: "/certifications", feature: "certifications" },
          { label: "Audit Logs", icon: Shield, href: "/audit-logs", feature: "audit_logs" },
          { label: "Compliance Alerts", icon: Bell, href: "/compliance-alerts", feature: null },
        ],
      },
    ],
  },
  {
    key: "fleet",
    name: "Fleet Management",
    icon: Truck,
    color: "text-cyan-600",
    bgColor: "bg-cyan-100",
    defaultExpanded: false,
    items: [
      { label: "Asset Registry", icon: Car, href: "/fleet?tab=vehicles", feature: "fleet" },
      { label: "Fleet & Asset Tracker", icon: Wrench, href: "/fleet-asset-tracker", feature: "fleet" },
      { label: "Maintenance Due", icon: Wrench, href: "/fleet?tab=maintenance", feature: "fleet" },
      { label: "Fuel & Usage", icon: Fuel, href: "/fleet?tab=fuel", feature: "fleet" },
      { label: "Safety & Compliance", icon: ShieldCheck, href: "/safety-documents", feature: "fleet" },
      { label: "Remarketing", icon: TrendingUp, href: "/fleet?tab=remarketing", feature: "fleet", underConstruction: true },
    ],
  },
  {
    key: "equipment",
    name: "Equipment Management",
    icon: Package,
    color: "text-orange-600",
    bgColor: "bg-orange-100",
    defaultExpanded: false,
    items: [
      { label: "Asset Registry", icon: Package, href: "/equipment?tab=registry", feature: "equipment", permission: "can_view_equipment" },
      { label: "Inspections Due", icon: Wrench, href: "/equipment?tab=inspections", feature: "equipment", permission: "can_view_equipment" },
      { label: "Safety & Compliance", icon: ShieldCheck, href: "/safety-documents", feature: "equipment", permission: "can_view_equipment" },
      { label: "Remarketing", icon: TrendingUp, href: "/equipment?tab=remarketing", feature: "equipment", underConstruction: true },
    ],
  },
];

/* ─── Sidebar Component ─── */
const Sidebar = ({ onClose }) => {
  const { user, logout, previewMode, exitPreview, effectiveRole, isQuoter, isPlatformAdminMode } = useAuth();
  const { isAdmin, hasFeature, features } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize expanded state: Operations expanded by default
  const [expandedModules, setExpandedModules] = useState(() => {
    const initial = {};
    MODULE_HIERARCHY.forEach(m => { initial[m.key] = m.defaultExpanded; });
    return initial;
  });
  const [expandedSubMenus, setExpandedSubMenus] = useState({});
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [isMaster, setIsMaster] = useState(false);
  const [ucDialog, setUcDialog] = useState({ open: false, name: "" });
  // Phase 19.4: per-user portal permissions for sidebar dimming
  const [portalPerms, setPortalPerms] = useState({ is_admin: true, permissions: {} });
  // Phase 19.24: persistent collapsed state for the bottom user-block.
  const [userSectionCollapsed, setUserSectionCollapsed] = useState(() => {
    try {
      return localStorage.getItem("bg.sidebar.userBlockCollapsed") === "1";
    } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("bg.sidebar.userBlockCollapsed", userSectionCollapsed ? "1" : "0"); } catch (e) { /* ignore */ }
  }, [userSectionCollapsed]);

  // Phase 19.24: business logo cascade — fetch tenant logo, surface to internal
  // owners always, and to portal users only when `apply_logo_to_portals` is on.
  const [businessLogoUrl, setBusinessLogoUrl] = useState(null);
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("token");
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${apiBase}/api/business/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (cancelled) return;
        const logo = data?.logo_url || null;
        const cascade = !!data?.apply_logo_to_portals;
        const isPortalUser = user.account_type === "portal" ||
          user.account_type === "client_portal" || user.account_type === "employee_portal";
        if (!logo) { setBusinessLogoUrl(null); return; }
        if (isPortalUser && !cascade) { setBusinessLogoUrl(null); return; }
        const url = logo.startsWith("http") ? logo : `${apiBase}${logo}?auth=${token}`;
        setBusinessLogoUrl(url);
      } catch (e) {
        /* ignore — no logo on failure */
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    const fetchPerms = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/portal/me/permissions`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          setPortalPerms(await res.json());
        }
      } catch {}
    };
    fetchPerms();
  }, [user]);

  const hasPortalPermission = (key) => {
    if (!key) return true;
    if (portalPerms.is_admin) return true;
    return !!portalPerms.permissions?.[key];
  };

  useEffect(() => {
    const checkAdminAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/access-check`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setHasAdminAccess(data.has_admin_access);
          setIsMaster(data.is_master);
        }
      } catch {}
    };
    checkAdminAccess();
  }, [user]);

  // Auto-expand the module group AND any sub-menu that contains the current path
  useEffect(() => {
    const path = location.pathname;
    MODULE_HIERARCHY.forEach(group => {
      const match = group.items.some(item => {
        if (path === item.href || path.startsWith(item.href + "/")) return true;
        return item.children?.some(c => path === c.href || path.startsWith(c.href + "/"));
      });
      if (match) {
        setExpandedModules(prev => ({ ...prev, [group.key]: true }));
      }
      // Auto-expand sub-menu when current path matches the sub-item OR any of its children
      group.items.forEach(item => {
        if (!item.children) return;
        const subMatch =
          path === item.href ||
          path.startsWith(item.href + "/") ||
          item.children.some(c => path === c.href || path.startsWith(c.href + "/"));
        if (subMatch) {
          setExpandedSubMenus(prev => ({ ...prev, [item.label]: true }));
        }
      });
    });
  }, [location.pathname]);

  const handleLogout = () => { logout(); navigate("/login"); };
  const handleNavClick = () => { if (onClose) onClose(); };
  const currentRole = effectiveRole || user?.role;

  const toggleModule = (key) => {
    setExpandedModules(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const toggleSubMenu = (key) => {
    setExpandedSubMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const canAccess = (feature) => {
    if (!feature) return true;
    return hasFeature(feature) || isAdmin;
  };

  const isActive = (href) => {
    if (href.includes("?")) {
      const [path, query] = href.split("?");
      return location.pathname === path && location.search.includes(query);
    }
    return location.pathname === href;
  };

  /* ─── Render a nav item (leaf) ─── */
  const renderNavItem = (item, depth = 1) => {
    if (!canAccess(item.feature)) return null;
    if (item.roles && !isAdmin) {
      // Phase 15: 'quoter' is a permission, not a role — grant access if the user has `can_create_quotes`
      const allowedByRole = item.roles.includes(currentRole)
        || (isQuoter && item.roles.includes("quoter"));
      if (!allowedByRole) return null;
    }

    const Icon = item.icon;
    // Phase 19.4: dim instead of hide when portal permission is missing
    const isPermLocked = !!item.permission && !hasPortalPermission(item.permission);

    if (item.underConstruction) {
      return (
        <button
          key={item.label}
          onClick={() => setUcDialog({ open: true, name: item.label })}
          className={cn(
            "flex items-center gap-2.5 w-full px-3 py-1.5 rounded-md text-sm transition-all duration-200",
            "text-muted-foreground/60 hover:bg-accent hover:text-accent-foreground",
            depth === 2 && "text-xs pl-4"
          )}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <Icon className="w-4 h-4" />
          <span className="flex-1 text-left">{item.label}</span>
          <Construction className="w-3 h-3 text-amber-500" />
        </button>
      );
    }

    if (isPermLocked) {
      return (
        <button
          key={item.label + item.href}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            toast.error("Access denied", {
              description: `You do not have the '${item.permission}' permission on this portal account.`,
            });
          }}
          className={cn(
            "flex items-center gap-2.5 w-full px-3 py-1.5 rounded-md text-sm transition-all duration-200 cursor-not-allowed",
            "text-muted-foreground/50 hover:bg-muted/30",
            depth === 2 && "text-xs pl-4"
          )}
          aria-disabled="true"
          title={`Locked: missing ${item.permission}`}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-locked`}
        >
          <Icon className="w-4 h-4 shrink-0 opacity-50" />
          <span className="truncate flex-1 text-left">{item.label}</span>
          <Lock className="w-3 h-3 text-muted-foreground/60 shrink-0" />
        </button>
      );
    }

    const href = item.href.split("?")[0];
    const search = item.href.includes("?") ? "?" + item.href.split("?")[1] : "";

    return (
      <NavLink
        key={item.label + item.href}
        to={item.href}
        onClick={handleNavClick}
        className={() =>
          cn(
            "flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-all duration-200",
            isActive(item.href)
              ? "bg-primary text-primary-foreground shadow-sm font-medium"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            depth === 2 && "text-xs pl-4"
          )
        }
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className="truncate">{item.label}</span>
      </NavLink>
    );
  };

  /* ─── Render item with optional children ─── */
  const renderItemWithChildren = (item) => {
    if (!canAccess(item.feature)) return null;

    if (!item.children) return renderNavItem(item, 1);

    // Phase 19.4: if parent has permission and user lacks it AND lacks all child permissions, dim parent too
    const parentLocked = !!item.permission && !hasPortalPermission(item.permission);
    const allChildrenLocked = parentLocked && item.children.every(
      (c) => c.permission && !hasPortalPermission(c.permission)
    );

    const subKey = item.label;
    const isSubExpanded = expandedSubMenus[subKey] ?? false;
    const Icon = item.icon;

    // Check if any child is active
    const hasActiveChild = item.children.some(c => isActive(c.href)) || isActive(item.href);

    return (
      <div key={item.label}>
        <button
          onClick={() => toggleSubMenu(subKey)}
          className={cn(
            "flex items-center justify-between w-full px-3 py-1.5 rounded-md text-sm transition-all duration-200",
            "hover:bg-accent hover:text-accent-foreground",
            hasActiveChild && "text-foreground font-medium",
            allChildrenLocked && "text-muted-foreground/50 hover:bg-muted/30"
          )}
          data-testid={`nav-sub-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <span className="flex items-center gap-2.5">
            <Icon className={cn("w-4 h-4 shrink-0", allChildrenLocked && "opacity-50")} />
            <span className="truncate">{item.label}</span>
            {allChildrenLocked && <Lock className="w-3 h-3 text-muted-foreground/60" />}
          </span>
          {isSubExpanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          )}
        </button>
        {isSubExpanded && (
          <div className="ml-3 pl-2 border-l border-border/40 space-y-0.5 mt-0.5">
            {item.children.map(child => renderNavItem(child, 2))}
          </div>
        )}
      </div>
    );
  };

  /* ─── Render a module group ─── */
  const renderModuleGroup = (group) => {
    // Filter items based on access
    const accessibleItems = group.items.filter(item => {
      if (isAdmin) return true;
      if (!item.feature) return true;
      return hasFeature(item.feature);
    });
    if (accessibleItems.length === 0 && !isAdmin) return null;

    const isExpanded = expandedModules[group.key] ?? false;
    const Icon = group.icon;

    return (
      <div key={group.key} className="mb-1">
        <button
          onClick={() => toggleModule(group.key)}
          className={cn(
            "flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
            "hover:bg-accent hover:text-accent-foreground",
            isExpanded && "bg-accent/50"
          )}
          data-testid={`module-toggle-${group.key}`}
        >
          <span className="flex items-center gap-2.5">
            <div className={cn("p-1 rounded", group.bgColor)}>
              <Icon className={cn("w-3.5 h-3.5", group.color)} />
            </div>
            <span className="text-foreground">{group.name}</span>
          </span>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
        {isExpanded && (
          <div className="mt-0.5 ml-2 pl-3 border-l border-border/50 space-y-0.5">
            {(isAdmin ? group.items : accessibleItems).map(renderItemWithChildren)}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={cn(
        "w-60 h-full border-r bg-card/50 backdrop-blur-xl z-30 flex flex-col",
        !onClose && "fixed hidden md:flex"
      )}
    >
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-accent md:hidden"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      {previewMode && (
        <div className="bg-amber-500 text-amber-950 px-3 py-2 flex items-center justify-between text-xs font-medium">
          <div className="flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" />
            <span>Preview: <span className="capitalize">{previewMode}</span></span>
          </div>
          <button
            onClick={exitPreview}
            className="hover:bg-amber-600/50 p-0.5 rounded"
            data-testid="sidebar-exit-preview"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Logo */}
      <div className="h-14 flex items-center px-4 border-b">
        <div className="flex items-center gap-2 min-w-0">
          <img
            src="/business-genie-logo.png"
            alt="Business Genie"
            className="w-8 h-8 rounded-lg object-contain bg-white/5 shrink-0"
            data-testid="sidebar-bg-logo"
          />
          <span className="font-heading font-bold text-lg text-foreground truncate">
            Business Genie
          </span>
          {/* Phase 19.24 — Business logo cascaded to portals */}
          {businessLogoUrl && (
            <>
              <span className="text-muted-foreground/40 shrink-0">·</span>
              <img
                src={businessLogoUrl}
                alt="Business logo"
                className="h-7 w-7 rounded object-contain bg-white border shrink-0"
                data-testid="sidebar-business-logo"
              />
            </>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 overflow-y-auto">
        {/* Dashboard - hidden for platform admins in their dedicated console mode */}
        {!isPlatformAdminMode && (
          <NavLink
            to="/dashboard"
            onClick={handleNavClick}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 mb-1",
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )
            }
            data-testid="nav-dashboard"
          >
            <LayoutDashboard className="w-4 h-4" />
            Dashboard
          </NavLink>
        )}

        {/* Module Groups — hidden for platform admin in their owner-console view */}
        {!isPlatformAdminMode && MODULE_HIERARCHY.map(renderModuleGroup)}

        {/* Unions - separate (only for Corporate package later) */}
        {!isPlatformAdminMode && (hasFeature("unions") || isAdmin) && (
          <div className="mt-1">
            <NavLink
              to="/union"
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
              data-testid="nav-unions"
            >
              <UsersRound className="w-4 h-4" />
              Unions
            </NavLink>
          </div>
        )}

        {/* Platform-admin owner-console nav (full list when in admin mode) */}
        {isPlatformAdminMode && (
          <div className="space-y-0.5" data-testid="admin-sidebar-nav">
            {[
              { tab: "analytics",         label: "Analytics",         icon: BarChart3 },
              { tab: "subscribers",       label: "Subscribers",       icon: Building2 },
              { tab: "portal-hierarchy",  label: "Portal Hierarchy",  icon: UsersRound },
              { tab: "notes",             label: "Notes",             icon: StickyNote },
              { tab: "internal-accounts", label: "Internal Accounts", icon: KeyRound },
              { tab: "licenses",          label: "Licenses",          icon: FileBadge },
              { tab: "ip",                label: "IP & Patents",      icon: Lightbulb },
              { tab: "howto",             label: "How-To Guide",      icon: BookOpen },
              { tab: "test-accounts",     label: "Test Accounts",     icon: TestTube2 },
              { tab: "tax",               label: "Tax Overview",      icon: Calculator },
              isMaster && { tab: "admins", label: "Admin Accounts",   icon: Shield },
            ].filter(Boolean).map(({ tab, label, icon: Icon }) => {
              const target = `/admin?tab=${tab}`;
              const isActive = location.pathname === "/admin" &&
                (new URLSearchParams(location.search).get("tab") || "analytics") === tab;
              return (
                <NavLink
                  key={tab}
                  to={target}
                  onClick={handleNavClick}
                  className={cn(
                    "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
                    isActive
                      ? "bg-amber-500 text-white shadow-sm"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  )}
                  data-testid={`admin-nav-${tab}`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="truncate">{label}</span>
                </NavLink>
              );
            })}
            <NavLink
              to="/admin/legal"
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-amber-500 text-white shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
              data-testid="admin-nav-legal"
            >
              <ScrollText className="w-4 h-4 shrink-0" />
              <span className="truncate">Legal Docs</span>
            </NavLink>
            <NavLink
              to="/admin/comms"
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-amber-500 text-white shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
              data-testid="admin-nav-comms"
            >
              <Megaphone className="w-4 h-4 shrink-0" />
              <span className="truncate">Owner Comms</span>
            </NavLink>
          </div>
        )}

        {/* Communications hub for office_managers (mounted at /admin/communications but
            allowed for any internal admin via Layout's exemption). */}
        {isAdmin && !isPlatformAdminMode && (
          <div className="pt-1 mt-1">
            <NavLink
              to="/admin/communications"
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-emerald-500 text-white shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
              data-testid="nav-communications"
            >
              <MessageSquare className="w-4 h-4 shrink-0" />
              <span className="truncate">Communications</span>
            </NavLink>
            <NavLink
              to="/universal-templates"
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-emerald-500 text-white shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
              data-testid="nav-universal-templates"
            >
              <FileStack className="w-4 h-4 shrink-0" />
              <span className="truncate">Templates</span>
            </NavLink>
            <NavLink
              to="/upload-extract"
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-emerald-500 text-white shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
              data-testid="nav-upload-extract"
            >
              <Upload className="w-4 h-4 shrink-0" />
              <span className="truncate">Upload & Extract</span>
            </NavLink>
            <NavLink
              to="/onboarding-wizard"
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-emerald-500 text-white shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
              data-testid="nav-onboarding-wizard"
            >
              <Sparkles className="w-4 h-4 shrink-0" />
              <span className="truncate">Onboarding Wizard</span>
            </NavLink>
          </div>
        )}

        {/* Master Admin entry (only shown when NOT already in admin sidebar mode) */}
        {hasAdminAccess && !isPlatformAdminMode && (
          <div className="pt-2 mt-1">
            <NavLink
              to="/admin"
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-amber-500 text-white shadow-sm"
                    : "text-amber-600 hover:bg-amber-500/10 hover:text-amber-700"
                )
              }
              data-testid="nav-admin-dashboard"
            >
              <Crown className="w-4 h-4" />
              Admin Controls
            </NavLink>
          </div>
        )}
      </nav>

      {/* User Section — Phase 19.24: single collapsible block with avatar upload */}
      <UserSection
        user={user}
        userSectionCollapsed={userSectionCollapsed}
        setUserSectionCollapsed={setUserSectionCollapsed}
        handleNavClick={handleNavClick}
        handleLogout={handleLogout}
      />

      {/* Under Construction Dialog */}
      <UnderConstructionDialog
        open={ucDialog.open}
        onOpenChange={(open) => setUcDialog({ ...ucDialog, open })}
        featureName={ucDialog.name}
      />
    </aside>
  );
};

/* ─── User Section (Phase 19.24 · iter147 UX rework) ─── */
/* Avatar + name + role are ALWAYS visible. The chevron only toggles the
   Business Settings + Sign Out menu items below — never hides the name. */
const UserSection = ({ user, userSectionCollapsed, setUserSectionCollapsed, handleNavClick, handleLogout }) => {
  const [avatarDialogOpen, setAvatarDialogOpen] = React.useState(false);
  const token = (typeof window !== "undefined") ? localStorage.getItem("token") : null;
  const rawAvatar = user?.avatar_url || null;
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const avatarUrl = rawAvatar
    ? (rawAvatar.startsWith("http") ? rawAvatar : `${apiBase}${rawAvatar}?auth=${token}`)
    : null;
  const initial = (user?.display_name || user?.name)?.charAt(0).toUpperCase() || "?";
  const menuExpanded = !userSectionCollapsed;

  return (
    <div className="p-2 border-t" data-testid="user-section">
      {/* Row 1 — Avatar + name + role — ALWAYS visible */}
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setAvatarDialogOpen(true)}
          className="relative w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden group/avatar shrink-0 hover:ring-2 hover:ring-primary/40 transition"
          title="Change profile picture"
          data-testid="user-avatar-btn"
          aria-label="Change profile picture"
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
          ) : (
            <span className="text-xs font-semibold text-primary">{initial}</span>
          )}
          <span className="absolute inset-0 bg-black/50 opacity-0 group-hover/avatar:opacity-100 flex items-center justify-center transition">
            <Camera className="w-3.5 h-3.5 text-white" />
          </span>
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium truncate">{user?.display_name || user?.name}</p>
            {user?.is_platform_admin && (
              <span
                title="Manager"
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0.5 text-[10px] font-semibold leading-none shrink-0"
                data-testid="platform-admin-pill"
              >
                <Crown className="w-2.5 h-2.5" />
                Manager
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground capitalize">
            {user?.role?.replace("_", " ")}
          </p>
        </div>
        {/* Chevron only toggles the menu items below — NEVER hides the name */}
        <button
          type="button"
          onClick={() => setUserSectionCollapsed((v) => !v)}
          className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 transition-colors shrink-0"
          title={menuExpanded ? "Hide menu options" : "Show menu options"}
          data-testid="user-section-toggle-btn"
          aria-label={menuExpanded ? "Hide menu options" : "Show menu options"}
          aria-expanded={menuExpanded}
        >
          {menuExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Row 2 — collapsible menu items (Business Settings + Sign Out) */}
      {menuExpanded && (
        <div data-testid="user-section-menu">
          <NavLink
            to="/profile"
            onClick={handleNavClick}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
            data-testid="profile-settings-link"
          >
            <Settings2 className="w-4 h-4" />
            Business Settings
          </NavLink>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      )}

      <AvatarUploadDialog
        open={avatarDialogOpen}
        onOpenChange={setAvatarDialogOpen}
        currentUrl={rawAvatar}
      />
    </div>
  );
};

export default Sidebar;
